import React, { useEffect, useState } from "react"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material"
import GroupAddIcon from "@mui/icons-material/GroupAdd"
import { useSnackbar, useCompany } from "src/providers"
import { getCompanyMembers, type CompanyMember } from "src/services/api/companyMembers"
import { addUserToGatedCommunity, getGatedCommunityMembers } from "src/services/api/gatedCommunities"
import { Community } from "src/models/user"
import CustomAutocomplete from "src/components/CustomAutocomplete"

interface AddMembersToCommunityDialogProps {
  open: boolean
  community: Community | null
  onClose: () => void
  onAdded: () => void
}

export const AddMembersToCommunityDialog = ({ open, community, onClose, onAdded }: AddMembersToCommunityDialogProps) => {
  const { snackbarShowMessage } = useSnackbar()
  const { companyId } = useCompany()
  const [memberOptions, setMemberOptions] = useState<CompanyMember[]>([])
  const [selectedMembers, setSelectedMembers] = useState<CompanyMember[]>([])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open || !community) return
    Promise.all([
      getCompanyMembers(companyId, { limit: 1000 }),
      getGatedCommunityMembers(community.gatedCommunityId, { limit: 1000 })
    ])
      .then(([companyRes, communityRes]) => {
        const existingIds = new Set(communityRes.data.map((m) => m.id))
        setMemberOptions(companyRes.results.filter((m) => m.userId && !existingIds.has(m.userId)))
      })
  }, [open])

  const handleClose = () => {
    setSelectedMembers([])
    onClose()
  }

  const handleAdd = () => {
    if (!community || selectedMembers.length === 0) return
    setSubmitting(true)
    Promise.all(selectedMembers.map((m) => addUserToGatedCommunity(community.gatedCommunityId, m.userId)))
      .then(() => {
        snackbarShowMessage("Member(s) added", "success")
        handleClose()
        onAdded()
      })
      .catch(() => snackbarShowMessage("Failed to add member(s)", "error"))
      .finally(() => setSubmitting(false))
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "25px", pt: 4, px: 4 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, fontSize: "32px", fontWeight: 600, px: 0 }}>
        <GroupAddIcon sx={{ fontSize: 36 }} />
        Add Members
      </DialogTitle>
      <DialogContent sx={{ px: 0, py: 2 }}>
        <Stack spacing={2}>
          {community && (
            <Typography variant="h6">
              Adding members to <strong>{community.gatedCommunityName}</strong>
            </Typography>
          )}
          <CustomAutocomplete<CompanyMember>
            label="Members *"
            multiple
            options={memberOptions}
            value={selectedMembers}
            getOptionLabel={(m) => `${m.firstName} ${m.lastName} (${m.email})`}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            onChange={(_, val) => setSelectedMembers((Array.isArray(val) ? val : []) as CompanyMember[])}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 0, pb: 2, pt: 3, gap: 0.5 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
          disabled={selectedMembers.length === 0 || submitting}
        >
          Add
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleClose}>
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  )
}
